import planck from 'planck-js';
import * as PIXI from 'pixi.js';
import GSObject from '../Base/GSObject';
import Vector from '../Base/Math/Vector';
import Global from '../Global';
import { CollisionGroup, CollisionCategory } from '../Collision';
import AssetManager from '../Base/AssetManager';
import LibraryManager from '../Base/Library/LibraryManager';
import { ViewDirection } from '../../GameKit/shared';
import { AnimatedNumber, AnimatedVector, EasingFunctions } from '../Base/Animate';

export default class Player extends GSObject {
    constructor(gsoParent) {
        super(gsoParent);

        this.level = gsoParent;

        this.sprite = null;
        this.hatSprite = null;
        this.spriteOverlay = null;
        this.pixiContainer = null;
        this.circleMaskGraphics = null;

        this.physicsBody = null;
        this.physicsShape = null;

        this.position = new Vector(100, Global.NATIVE_LEVEL_HEIGHT * Global.NATIVE_BLOCK_SIZE - 100);
        this.size = new Vector(1, 1);
        this.rotation = 0;

        // Jump variables
        this.isGrounded = false;
        this.inJump = false;
        this.offGroundTime = 0; // Milliseconds
        this.jumpTime = 0; // Milliseconds
        this.maxJumpLength = 260; // Milliseconds
        this.coyoteTime = 160; // Milliseconds

        this.isPlayer = true;

        this.damageAnimation = new AnimatedVector(this, EasingFunctions.easeInSine, new Vector(1, 1), new Vector(1.15, 1), 0.1);
        this.damageAnimation.pong(EasingFunctions.easeOutSine);
        this.damageColorAnimation = new AnimatedNumber(this, EasingFunctions.easeOutCubic, 0, 1, 0.1);
        this.damageColorAnimation.pong(EasingFunctions.easeOutCubic);

        this.rewardAnimation = new AnimatedVector(this, EasingFunctions.easeInSine, new Vector(1, 1), new Vector(0.9, 1.1), 0.1);
        this.rewardAnimation.pong(EasingFunctions.easeOutCubic);
    }

    onCreateVisuals() {
        const game = this.getGame();

        this.pixiContainer = new PIXI.Container();
        this.pixiContainer.x = this.position.x;
        this.pixiContainer.y = this.position.y;
        this.pixiContainer.rotation = this.rotation;
        this._gsoParent.pixiContainerPlayerLayer.addChild(this.pixiContainer);

        this.sprite = new PIXI.Sprite(
            LibraryManager.textures
                .get(game.playerSettings.texture)
                .getAsset()
                .getHue(game.playerSettings.hue),
            64,
            64
        );
        this.sprite.anchor.set(0.5);
        const spriteScale = (Global.NATIVE_BLOCK_SIZE * this.size.x) / this.sprite.width;
        this.sprite.width = Global.NATIVE_BLOCK_SIZE * this.size.x;
        this.sprite.height = Global.NATIVE_BLOCK_SIZE * this.size.x;

        this.circleMaskGraphics = new PIXI.Graphics();
        this.circleMaskGraphics.beginFill(0x000000);
        this.circleMaskGraphics.drawCircle(0, 0, this.sprite.texture.width / 2);
        this.circleMaskGraphics.endFill();

        this.sprite.mask = this.circleMaskGraphics;
        this.sprite.addChild(this.circleMaskGraphics);
        this.pixiContainer.addChild(this.sprite);

        // DEBUG PURPOSES ONLY
        const self = this;
        this.sprite.texture.baseTexture.on('loaded', () => {
            self.requestNewVisuals();
        });

        this.spriteOverlay = new PIXI.Sprite(AssetManager.get('img.player_overlay'));
        this.spriteOverlay.anchor.set(0.5);
        this.spriteOverlay.width = Global.NATIVE_BLOCK_SIZE * this.size.x;
        this.spriteOverlay.height = Global.NATIVE_BLOCK_SIZE * this.size.x;
        this.pixiContainer.addChild(this.spriteOverlay);

        const hatEntry = LibraryManager.hats.get(game.playerSettings.hat);
        if (this.level.viewDirection === ViewDirection.Side) {
            this.hatSprite = new PIXI.Sprite(hatEntry.getSide());
            this.hatSprite.anchor.set(0.5);
            this.hatSprite.anchor.x += hatEntry.posSide.x / this.hatSprite.width;
            this.hatSprite.anchor.y += hatEntry.posSide.y / this.hatSprite.height;
            this.hatSprite.width *= spriteScale;
            this.hatSprite.height *= spriteScale;
            this.hatSprite.y = (-Global.NATIVE_BLOCK_SIZE * this.size.x) / 2;
            this.pixiContainer.addChild(this.hatSprite);
        } else {
            this.hatSprite = new PIXI.Sprite(hatEntry.getTop());
            this.hatSprite.anchor.set(0.5);
            this.hatSprite.anchor.x += hatEntry.posTop.x / this.hatSprite.width;
            this.hatSprite.anchor.y += hatEntry.posTop.y / this.hatSprite.height;
            this.hatSprite.width *= spriteScale;
            this.hatSprite.height *= spriteScale;
            this.pixiContainer.addChild(this.hatSprite);
        }
    }

    onUpdateVisuals() {
        if (this._hasBody) {
            // If we have a physics presence then use that info.
            this.pixiContainer.x = this.getGame().deltaLerp(this.pixiContainer.x, this.physicsBody.getPosition().x * Global.PHYSICS_SCALE);
            this.pixiContainer.y = this.getGame().deltaLerp(this.pixiContainer.y, this.physicsBody.getPosition().y * Global.PHYSICS_SCALE);
            this.sprite.rotation = this.getGame().angleDeltaLerp(this.sprite.rotation, this.physicsBody.getAngle());

            this.pixiContainer.scale.x = this.damageAnimation.value.x * this.rewardAnimation.value.x;
            this.pixiContainer.scale.y = this.damageAnimation.value.y * this.rewardAnimation.value.y;
            this.sprite.tint = 0xffffff - this.damageColorAnimation.value * 0x003333;
            this.hatSprite.tint = 0xffffff - this.damageColorAnimation.value * 0x003333;
        } else {
            this.pixiContainer.x = this.position.x;
            this.pixiContainer.y = this.position.y;
            this.sprite.rotation = this.rotation;
        }
    }

    onDestroyVisuals() {
        this.pixiContainer.destroy();
        this.circleMaskGraphics.destroy();
        this.sprite.destroy();
        this.spriteOverlay.destroy();
    }

    onCreateBody(physicsWorld) {
        const game = this.getGame();

        this.physicsBody = physicsWorld.createBody().setDynamic();
        this.physicsBody.setPosition(planck.Vec2(this.position.x / Global.PHYSICS_SCALE, this.position.y / Global.PHYSICS_SCALE));
        this.physicsBody.setAngle(this.rotation);
        this.physicsShape = planck.Circle((Global.NATIVE_BLOCK_SIZE / Global.PHYSICS_SCALE / 2) * 0.97);
        this.physicsBody.createFixture(this.physicsShape, {
            friction: 0.9,
            density: game.playerSettings.weight,
            restitution: game.playerSettings.bounce,
            filterGroupIndex: CollisionGroup.none,
            filterCategoryBits: CollisionCategory.dynamic,
            filterMaskBits: CollisionCategory.dynamic + CollisionCategory.static
        });
        if (this.level.viewDirection === ViewDirection.Side) {
            this.physicsBody.setLinearDamping(0.2);
            this.physicsBody.setAngularDamping(0.2);
        } else {
            this.physicsBody.setLinearDamping(3.5);
            this.physicsBody.setAngularDamping(2.5);
        }
    }

    onDestroyBody(physicsWorld) {
        physicsWorld.destroyBody(this.physicsBody);
    }

    onFixedUpdate(deltaTime) {
        if (this._hasBody) {
            const game = this.getGame();

            if (!game.paused) {
                const position = Vector.fromPlanckVec2(this.physicsBody.getPosition());
                position.mulm(new Vector(Global.PHYSICS_SCALE));
                position.divm(new Vector(Global.NATIVE_BLOCK_SIZE));
                if (
                    position.x < this.level.position.x ||
                    position.y < this.level.position.y ||
                    position.x >= this.level.position.x + this.level.size.x ||
                    position.y >= this.level.position.y + this.level.size.y
                ) {
                    window.GamePlayer.instance.modifyHealth(-5);
                }
            }

            const speedModifier = 0.3;
            const jumpModifier = 0.3;

            const maxSpeed = 10;
            let maxSpeedModifierLeft = 1.0;
            if (this.physicsBody.m_linearVelocity.x / Global.PHYSICS_SCALE_OG_FACTOR < -maxSpeed) {
                maxSpeedModifierLeft /= (-this.physicsBody.m_linearVelocity.x / Global.PHYSICS_SCALE_OG_FACTOR - maxSpeed) * 2 + 1;
            }
            let maxSpeedModifierRight = 1.0;
            if (this.physicsBody.m_linearVelocity.x / Global.PHYSICS_SCALE_OG_FACTOR > maxSpeed) {
                maxSpeedModifierRight /= (this.physicsBody.m_linearVelocity.x / Global.PHYSICS_SCALE_OG_FACTOR - maxSpeed) * 2 + 1;
            }

            if (this.level.viewDirection === ViewDirection.Side) {
                if (this.inJump && this.physicsBody.m_linearVelocity.y > 0) {
                    this.inJump = false;
                }

                if (this.isGrounded && !this.inJump) {
                    this.offGroundTime = 0;
                } else {
                    this.offGroundTime += deltaTime;
                }

                let inAirModifier = 0.8;
                if (this.offGroundTime < this.coyoteTime) {
                    inAirModifier = 1.0;
                }

                if (game.keys.ArrowLeft) {
                    // Left
                    this.physicsBody.applyLinearImpulse(
                        planck.Vec2(-0.1 * deltaTime * maxSpeedModifierLeft * inAirModifier * speedModifier * Global.PHYSICS_SCALE_OG_FACTOR, 0),
                        this.physicsBody.getPosition(),
                        true
                    );
                }
                if (game.inputVector.x < 0) {
                    // Left
                    this.physicsBody.applyLinearImpulse(
                        planck.Vec2(0.1 * deltaTime * game.inputVector.x * maxSpeedModifierLeft * inAirModifier * speedModifier * Global.PHYSICS_SCALE_OG_FACTOR, 0),
                        this.physicsBody.getPosition(),
                        true
                    );
                }

                if (game.keys.ArrowRight) {
                    // Right
                    this.physicsBody.applyLinearImpulse(
                        planck.Vec2(0.1 * deltaTime * maxSpeedModifierRight * inAirModifier * speedModifier * Global.PHYSICS_SCALE_OG_FACTOR, 0),
                        this.physicsBody.getPosition(),
                        true
                    );
                }
                if (game.inputVector.x > 0) {
                    // Left
                    this.physicsBody.applyLinearImpulse(
                        planck.Vec2(0.1 * deltaTime * game.inputVector.x * maxSpeedModifierRight * inAirModifier * speedModifier * Global.PHYSICS_SCALE_OG_FACTOR, 0),
                        this.physicsBody.getPosition(),
                        true
                    );
                }

                if (game.keys.ArrowUp || game.keys[' ']) {
                    // Up
                    if (this.offGroundTime < this.coyoteTime && !this.inJump) {
                        this.physicsBody.applyLinearImpulse(planck.Vec2(0, -32 * Global.PHYSICS_SCALE_OG_FACTOR * jumpModifier), this.physicsBody.getPosition(), true);
                        this.isGrounded = false;
                        this.jumpTime = 0;
                        this.inJump = true;
                        this.offGroundTime = this.coyoteTime;
                    } else if (this.jumpTime < this.maxJumpLength && this.inJump) {
                        this.physicsBody.applyLinearImpulse(planck.Vec2(0, -0.09 * deltaTime * Global.PHYSICS_SCALE_OG_FACTOR * jumpModifier), this.physicsBody.getPosition(), true);
                        this.isGrounded = false;
                        this.jumpTime += deltaTime;
                    } else {
                        this.inJump = false;
                    }
                } else {
                    this.inJump = false;
                }
                if (game.keys.ArrowDown) {
                    // Down
                }
                this.isGrounded = false;
            } else {
                let maxSpeedModifierUp = 1.0;
                if (this.physicsBody.m_linearVelocity.y / Global.PHYSICS_SCALE_OG_FACTOR < -maxSpeed) {
                    maxSpeedModifierUp /= (-this.physicsBody.m_linearVelocity.y / Global.PHYSICS_SCALE_OG_FACTOR - maxSpeed) * 2 + 1;
                }
                let maxSpeedModifierDown = 1.0;
                if (this.physicsBody.m_linearVelocity.y / Global.PHYSICS_SCALE_OG_FACTOR > maxSpeed) {
                    maxSpeedModifierDown /= (this.physicsBody.m_linearVelocity.y / Global.PHYSICS_SCALE_OG_FACTOR - maxSpeed) * 2 + 1;
                }

                if (game.keys.ArrowLeft) {
                    // Left
                    this.physicsBody.applyLinearImpulse(
                        planck.Vec2(-0.15 * deltaTime * maxSpeedModifierLeft * speedModifier * Global.PHYSICS_SCALE_OG_FACTOR, 0),
                        this.physicsBody.getPosition(),
                        true
                    );
                }
                if (game.keys.ArrowUp) {
                    // Left
                    this.physicsBody.applyLinearImpulse(planck.Vec2(0, -0.15 * deltaTime * maxSpeedModifierUp * speedModifier * Global.PHYSICS_SCALE_OG_FACTOR), this.physicsBody.getPosition(), true);
                }
                if (game.keys.ArrowDown) {
                    // Left
                    this.physicsBody.applyLinearImpulse(planck.Vec2(0, 0.15 * deltaTime * maxSpeedModifierDown * speedModifier * Global.PHYSICS_SCALE_OG_FACTOR), this.physicsBody.getPosition(), true);
                }
                if (game.keys.ArrowRight) {
                    // Right
                    this.physicsBody.applyLinearImpulse(
                        planck.Vec2(0.15 * deltaTime * maxSpeedModifierRight * speedModifier * Global.PHYSICS_SCALE_OG_FACTOR, 0),
                        this.physicsBody.getPosition(),
                        true
                    );
                }

                if (game.inputVector.x !== 0) {
                    // Horizontal
                    this.physicsBody.applyLinearImpulse(
                        planck.Vec2(0.15 * deltaTime * game.inputVector.x * maxSpeedModifierLeft * maxSpeedModifierRight * speedModifier * Global.PHYSICS_SCALE_OG_FACTOR, 0),
                        this.physicsBody.getPosition(),
                        true
                    );
                }
                if (game.inputVector.y !== 0) {
                    // Vertical
                    this.physicsBody.applyLinearImpulse(
                        planck.Vec2(0, 0.15 * deltaTime * game.inputVector.y * maxSpeedModifierUp * maxSpeedModifierDown * speedModifier * Global.PHYSICS_SCALE_OG_FACTOR),
                        this.physicsBody.getPosition(),
                        true
                    );
                }
            }
        }
    }
}

Player.serializeableFields = {
    position: Vector,
    size: Vector,
    rotation: Number
};
